import { createContext, useContext, useEffect, useState, useCallback } from "react";

// Theme engine — swaps the `data-theme` attribute on <html>, persists choice
// to localStorage so it survives reload, and exposes a `setTheme` updater.
// Available themes: dark | light | gold | mono | blue | purple
// Default for NEW users (no saved preference): "blue" (Blue Neon). Existing
// users' saved choices are preserved untouched.
const THEMES = ["dark", "light", "gold", "mono", "blue", "purple"];
const LS_KEY = "glow_theme";
const DEFAULT_THEME = "blue";

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {} });

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_THEME;
    const saved = localStorage.getItem(LS_KEY);
    return THEMES.includes(saved) ? saved : DEFAULT_THEME;
  });

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!THEMES.includes(next)) return;
    setThemeState(next);
    try { localStorage.setItem(LS_KEY, next); } catch { /* ignore quota errors */ }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
